// Auto-generated from docs/architecture/flux-cold.md — do not edit manually
const content = `
# Flux COLD Architecture

Flux COLD is the cold storage / refrigeration thermal optimization domain within the Vortx platform. It optimizes compressor schedules for refrigerated facilities (warehouses, distribution centers, food processing plants, blast freezers) to minimize electricity costs by exploiting thermal mass as implicit energy storage.

## Overview

Cold storage facilities maintain low temperatures by running electrically-driven compressors. These compressors represent a significant, flexible electrical load because the thermal mass of the stored product acts as a buffer: the facility can be pre-cooled during cheap off-peak hours and then allowed to coast (compressors off) during expensive on-peak hours without violating food safety temperature limits.

Flux COLD captures this value through:

- **TOU energy shifting** -- pre-cool during off-peak, coast during on-peak
- **Peak demand reduction** -- stagger compressor operation across zones to reduce the coincident peak demand charge
- **Multi-agent coordination** -- participate in ADMM-based site-level optimization alongside battery and EV agents via the ColdAgent in flux-core

The MILP solver uses a first-principles RC (resistance-capacitance) thermal model for each zone, coupled with unit-commitment-style compressor constraints (min runtime, min off-time, variable-speed VFD power fractions).

## Architecture

\`\`\`
Frontend (flux-cold-storage-web)
    |
    | HTTPS (via Platform API proxy)
    v
Platform API (vortx-platform-api)
    |
    | /api/cold_storage/v1/*
    v
Flux COLD API (flux-cold-storage-api)
    |
    +-- HiGHS MILP solver (in-process, highspy)
    +-- OSQP QP solver (load disaggregation)
    +-- Firestore (project/scenario storage)
    +-- Cloud Storage (large optimization results)
    +-- NREL PVWatts (weather data)
\`\`\`

**Deployment**: Flask app on Cloud Run. 2 gunicorn workers, 4 threads each, 300s timeout. Python 3.11. HiGHS MILP solver compiled from source (requires cmake + build-essential).

**Frontend**: React workspace package (\`flux-cold-storage-web\`) compiled into \`flux-web\` via craco. Routed under \`/cold-storage\`.

## API Routes

All routes are prefixed with \`/api/v1/\` at the Flask level. The Platform API proxies them under \`/api/cold_storage/v1/\`.

### \`GET /health\`

Health check. Returns solver version.

### \`POST /api/v1/optimize\`

Core optimization endpoint. Runs the MILP solver for a single time window (24h to 744h).

**Request body:**
\`\`\`json
{
  "facility_config": {
    "facility_type": "cold_storage_warehouse",
    "zones": [...],
    "horizon_hours": 744,
    "timestep_hours": 0.25
  },
  "ambient_temps": [72.3, 72.5, ...],
  "tariff_id": "pge_e19_secondary",
  "start_hour": 0,
  "constraints": {
    "available_capacity_kw": [400, 400, ...]
  }
}
\`\`\`

Supports two tariff modes:
1. \`tariff_id\` -- fetches approved tariff from Firestore, converts to 8760 rates, slices to window
2. \`energy_prices_usd_kwh\` + \`demand_charge_usd_kw\` -- direct price arrays (legacy)

**Response:** Optimization result with per-zone temperature trajectories, compressor schedules, load curves, cost breakdown, baseline comparison, and savings.

### \`POST /api/v1/simulate\`

MPC simulation endpoint. Runs a receding-horizon simulation with separate forecast and actual ambient temperatures to evaluate robustness under forecast error.

**Request body:**
\`\`\`json
{
  "facility_config": {...},
  "simulation_hours": 72,
  "timestep_hours": 0.25,
  "mpc_horizon_hours": 4.0,
  "ambient_forecast": [...],
  "ambient_actual": [...],
  "energy_prices_usd_kwh": [...],
  "demand_charge_usd_kw": 15.0,
  "initial_temps": {"freezer_1": -10.0}
}
\`\`\`

### \`GET /api/v1/weather\`

Fetches 8760 hourly ambient temperatures from NREL PVWatts for a given location.

**Query params:** \`lat\`, \`lon\`

**Response:** \`hourly_temps_f\` (8760 values, Fahrenheit) and \`design_day_temps_f\` (24 values: average hourly profile of the hottest month).

### \`POST /api/v1/decompose\`

Load disaggregation endpoint. Extracts the cold storage electrical load component from a blended facility meter profile using a QP (OSQP) constrained by RC thermal dynamics.

**Request body:**
\`\`\`json
{
  "meter_load_kw": [...8760...],
  "location": {"lat": 33.83, "lng": -118.38},
  "zones": [...]
}
\`\`\`

**Response:** \`extracted_loads_kw\`, \`residual_loads_kw\`, \`confidence\` score, diagnostics (R-squared, RMSE, temperature correlation).

### Scenario CRUD Routes

All under \`/api/v1/projects/<project_id>/scenarios\`. Require Firebase Auth token.

| Method | Path | Description |
|--------|------|-------------|
| \`POST\` | \`/\` | Create scenario |
| \`GET\` | \`/\` | List scenarios |
| \`GET\` | \`/<scenario_id>\` | Get scenario (hydrates full results from GCS) |
| \`PUT\` | \`/<scenario_id>\` | Update scenario (uploads results to GCS) |
| \`DELETE\` | \`/<scenario_id>\` | Delete scenario + GCS results |
| \`POST\` | \`/<scenario_id>/duplicate\` | Duplicate scenario |

## Frontend Components

### Pages

| File | Description |
|------|-------------|
| \`AppRoot.jsx\` | Top-level router: \`/\` -> ProjectsPage, \`/:projectId\` -> ProjectEditor |
| \`ProjectsPage.jsx\` | Project list with expandable rows, create/rename/delete dialogs |
| \`ProjectEditor.jsx\` | Tabbed editor: scenario list, zones config, results. Uses RetroNavigation sections. |
| \`ColdStorageApp.jsx\` | Legacy routing wrapper (single ColdStorageOptimization page) |
| \`ColdStorageOptimization.jsx\` | Standalone optimization page with inline zone editor and results chart |

### Components

| File | Description |
|------|-------------|
| \`ZoneEditor.jsx\` | Facility configuration: type selector (warehouse, distribution center, food processing, blast freezer), building specs (sqft, R-value, ceiling height), auto-derived thermal R/C params, ASHRAE-based compressor auto-sizing, temperature deadband config, VFD min-power fraction |
| \`ResultsChart.jsx\` | Three-level drill-down: annual summary table (12 months), monthly detail (daily-aggregated charts), daily detail (quarter-hour load curve + temperature trajectory). Shows baseline vs optimized comparison, cost breakdown, savings. |
| \`ScenarioList.jsx\` | Scenario picker within a project |
| \`SectionNav.jsx\` | Section navigation component |

### Section Components

| File | Description |
|------|-------------|
| \`sections/ZonesSection.jsx\` | Wraps ZoneEditor with weather data display (design day temperature chart from NREL), meter info banner, load relationship selector (included vs. additive) |
| \`sections/ResultsSection.jsx\` | Pre-flight checks (zones configured, tariff selected, weather loaded), optimization trigger button with progress bar, results display |
| \`sections/SiteSection.jsx\` | Standard site location form (address, city, state, ZIP) |

### Hooks

| Hook | Description |
|------|-------------|
| \`useColdOptimization\` | Manages 12-month annual optimization: iterates through months, calls optimize API for each, computes annual summary, tracks progress |
| \`useColdProject\` | Single project management: loads project from unified projects API, auto-enables flux_cold app, auto-save, auto-decompose when meter load + zones available, input fingerprinting for stale-results detection |
| \`useColdProjects\` | Wrapper around \`useUnifiedProjects('flux_cold')\` for CRUD |
| \`useColdScenarios\` | Scenario list/selection/CRUD with auto-create default scenario, GCS-hydrated results fetch on selection |
| \`useWeatherData\` | Fetches 8760 hourly temps + design day from \`/api/cold_storage/v1/weather\` |

### Services

| Service | Description |
|---------|-------------|
| \`coldStorageService.js\` | \`optimizeFacility()\` -- interpolates hourly -> quarter-hour ambient temps, calls optimize API. \`decomposeMeterLoad()\` -- calls decompose API. |
| \`coldScenarioService.js\` | Scenario CRUD API calls through Platform API proxy |

## MILP Solver

**File:** \`apps/flux-cold-storage-api/solver/milp.py\`

**Solver:** HiGHS (via highspy). MILP with binary compressor commitment variables.

### Decision Variables (per zone, per timestep t=0..N-1)

| Variable | Type | Bounds | Description |
|----------|------|--------|-------------|
| \`u[t]\` | Binary | {0, 1} | Compressor on/off commitment |
| \`v[t]\` | Continuous | [0, 1] | Startup indicator |
| \`w[t]\` | Continuous | [0, 1] | Shutdown indicator |
| \`p[t]\` | Continuous | [0, 1] | Power fraction (VFD) |
| \`T_norm[t]\` | Continuous | [0, 1] | Normalized zone temperature |

**Global variables:**
| Variable | Type | Description |
|----------|------|-------------|
| \`P_peak\` | Continuous | Auxiliary variable for demand charge linearization |
| \`w_pwl[t]\` | Continuous (>=0) | ADMM PWL epigraph variables (K=8 tangent lines) |

### Thermal Dynamics Model (R-C)

Single-node resistance-capacitance model. Temperature is normalized to [0,1] for numerical conditioning.

**Physical model:**
\`\`\`
T[t+1] = (1 - alpha) * T[t] + alpha * T_amb[t] + gamma - beta * p[t]
\`\`\`

where:
- \`alpha = dt / (R * C)\` -- ratio of timestep to thermal time constant
- \`beta = dt * COP * P_rated * 3.412 / C\` -- cooling effectiveness (normalized: beta / T_range)
- \`gamma = dt * Q_internal / C\` -- internal heat gains (normalized: gamma / T_range)
- \`T_range = T_hi - T_lo\` (temperature deadband in F)
- \`T_norm = (T - T_lo) / T_range\`

**Internal heat gains** (infiltration, product loads, forklifts) are not captured by the envelope-only RC model. They are derived from the gap between compressor sizing capacity and envelope-only heat load at the ASHRAE 0.4% design day (105 F):

\`\`\`
Q_internal = max(0, COP * P_rated * 3.412 - (T_design - T_hi) / R)
\`\`\`

### Constraint Formulation

**(1) Initial temperature:**
\`\`\`
T_norm[0] = T_init_norm
\`\`\`

**(2) Thermal dynamics (normalized):**
\`\`\`
T[t+1] - (1-alpha)*T[t] + beta_norm*p[t] = alpha*T_amb_norm[t] + gamma_norm
\`\`\`

**(3) Logical linking (Morales-Espana 3-binary formulation):**
\`\`\`
u[t] - u[t-1] = v[t] - w[t]     for t >= 1
u[0] = v[0] - w[0]               (assume compressor starts OFF)
\`\`\`

**(4) Mutual exclusion:**
\`\`\`
v[t] + w[t] <= 1
\`\`\`

**(5) Minimum uptime (Rajan-Takriti convex hull):**
\`\`\`
sum_{i=max(0,t-UT+1)}^{t} v[i] <= u[t]
\`\`\`

**(6) Minimum downtime (Rajan-Takriti convex hull):**
\`\`\`
sum_{i=max(0,t-DT+1)}^{t} w[i] + u[t] <= 1
\`\`\`

**(7) Power-commitment linking (VFD variable-speed):**
\`\`\`
p[t] <= u[t]                     (can't run when off)
p[t] >= min_power * u[t]         (must run at min_power when on)
\`\`\`

**(8) Peak demand linearization (system-aware):**
\`\`\`
P_peak >= sum_z(P_rated_z * p_z[t]) + other_agents_power[t]     for all t
\`\`\`

When \`other_agents_power_kw\` is provided, the peak tracks the system peak (cold + others), so the demand charge correctly reflects cold's contribution to the combined site peak.

**(9) Capacity constraints (optional orchestrator limits):**
\`\`\`
sum_z(P_rated_z * p_z[t]) <= capacity_limit_kw[t]
\`\`\`

### Objective Function

\`\`\`
minimize: sum_t [price[t] * sum_z(P_rated_z * p_z[t]) * dt]   (energy cost)
        + demand_charge * P_peak                                (demand cost)
        + sum_t [congestion[t] * sum_z(P_rated_z * p_z[t])]   (congestion pricing)
        + sum_t [w_pwl[t]]                                      (ADMM quadratic penalty)
\`\`\`

### ADMM Piecewise-Linear (PWL) Approximation

Since the MILP has binary variables, the ADMM quadratic penalty \`(rho/2)(x - target)^2\` cannot be added directly to the objective. Instead, it is approximated via K=8 piecewise-linear tangent lines (epigraph formulation):

\`\`\`
w[t] >= slope_k * total_power[t] + intercept_k    for k=0..7
\`\`\`

where at evaluation point \`x_k = total_max_power * (k + 0.5) / K\`:
\`\`\`
slope_k = rho * (x_k - target[t])
intercept_k = (rho/2)(x_k - target[t])^2 - slope_k * x_k
\`\`\`

### Warm Starting

Two warm-start strategies:

1. **Bang-bang heuristic** (default): Hysteresis controller with thresholds at T_norm > 0.7 (ON) and T_norm < 0.3 (OFF), respecting min up/down times. Power fraction is set to the steady-state estimate.

---
*This page is auto-generated from \`docs/architecture/\`. See the full document for complete details.*
`;

export default content;
